<template>
    <div class="">
        <div class="mb-3" v-if="errors && errors.length > 0">
            <div class="alert alert-danger alert-dismissable fade show" role="alert">
                <button type="button" class="btn-close float-end" data-dismiss="alert" aria-label="Close" onclick="closeAlert(this)">
                </button>
                <p>An error was encountered while trying to submit the file. Please try again and/or contact your administrator with the following errors:</p>
                <ul class="list-group">
                    <li v-for="error in errors">
                        {{ error }}
                    </li>
                </ul>
            </div>
        </div>
           
        <tab-layout v-model="activeTab" v-bind:tabs="tabs" :header-left-label="headerLeft" :header-right-label="headerRight" @submit="handleContactsSubmit" @updateTab="handleUpdateTab" :cancel-button-url="defaultUrl">
            <component :is="activeTab" ref="tabComponent"></component>
        </tab-layout>
    </div>    
</template>
<script lang="ts">
    import { defineComponent, defineAsyncComponent, ref } from 'vue';
    import TabLayout from './TabLayout/TabLayout.vue';
    import { Tab } from './TabLayout/TabLayout.types';
    import Incident from './ContactsEditor/Incident.vue';
    import Citizen from './ContactsEditor/Citizen.vue';
    import Officer from './ContactsEditor/Officer.vue';
    import { useContactsStore } from '../stores/contactsStore';
import { useOriStore } from '@/stores/oriStore';
import axios, { AxiosResponse } from 'axios';
import { FullReport } from './ContactsEditor/schema';
import { addIdPropertyToTargetArrays } from '@/utils';

    export default defineComponent({
        components: {
            TabLayout,
            Incident,
            Citizen,
            Officer
        },
        props: {
            incidentNumber:String,
            ori:String,
            uploadUrl:String,
            verificationUrl:String,
            defaultUrl:String
        },
        data() {
            return {
                activeTab:'incident',
                errors: [] as string[]
            }
        },
        setup(props) {
            let form = document.getElementById("mainForm");
            if(form){
                form.classList.add('needs-validation'); 
            }         
            
            if(props.incidentNumber == undefined || props.ori == undefined){
                window.location.href = `ContactsIncidentSelector.aspx?error=missingparameter`;
            }

            const contactsStore = useContactsStore();

            const oriStore = useOriStore();
            
            if(contactsStore.contactFullReport == undefined || (contactsStore.contactFullReport.incident_number?.toLowerCase() != props.incidentNumber!.toLowerCase() && contactsStore.contactFullReport.agency_ori?.toLowerCase() != props.ori!.toLowerCase())) {
                contactsStore.fetchContacts(props.incidentNumber!, props.ori!).then((response)=> {
                    contactsStore.contactFullReport = response.data;
                    addIdPropertyToTargetArrays(contactsStore.contactFullReport);
                });
            }
            
            oriStore.fetchOris().then(() => {
                oriStore.setSelectedOri(props.ori!);
            });
                  
            contactsStore.fetchAges();

            contactsStore.fetchCodeLabelByKind();
                        
            if(contactsStore.contactFullReport == undefined) {
                window.location.href = `ContactsIncidentSelector.aspx?error=notfound&incident=${encodeURIComponent(props.incidentNumber ?? "")}&ori=${encodeURIComponent(props.ori  ?? "")}`;
            }
            
            const tabComponent = ref()
            

            return { contactsStore, oriStore, tabComponent };
        },
        computed: {
            tabs():Tab[] {

                let data:Tab[] = [
                    {
                        id:'1',
                        componentName:'incident',
                        displayText:"Incident"
                    },
                    {
                        id:'2',
                        componentName:'citizen',
                        displayText:"Citizen"
                    },
                    {
                        id:'3',
                        componentName:'officer',
                        displayText:"Officer"
                    }
                ];

                return data;
            },
            headerLeft():string {
                return `ORI: ${this.ori} (${this.oriStore.getOriName})`;
            },
            headerRight():string {
                return `Incident Number: ${this.incidentNumber}`;
            },
            uploadHeaders():any {
                return {
                    "adminOnly": false,
                    "Content-Type": "multipart/form-data",
                };
            },
        },
        methods: {
            async handleContactsSubmit(){                  
                if(this.validate() && this.uploadUrl != undefined && this.verificationUrl != undefined){
                    let formData = new FormData();                    
                    
                    let data = JSON.stringify(this.contactsStore.contactFullReport);
                    
                    let response = await axios.post(this.verificationUrl, data, {
                        headers: {"Content-Type": "application/json"}                        
                    });
                    if(response){
                        
                        //Display validation messages
                        if(Array.isArray(response.data))
                        {
                            response.data.forEach((f:any) => this.errors.push(`${f.Code} - ${f.Message} - ${f.Description}`));
                            
                            if(this.errors.length > 0) {
                            
                                scrollTo(0,0);
                            
                            } 
                        }
                        //If we got submitable json back then well use it to create a file and upload it
                        else if (response.data?.specification != undefined) {
                            let fileName = `Contact.${this.ori}.${this.incidentNumber}.json`;
                        
                            let f = new File([JSON.stringify(response.data)] as string[], fileName, {
                                type: "application/json"
                            });

                            formData.append("files", f, fileName);

                            await axios.post(this.uploadUrl, formData, {
                                headers: this.uploadHeaders,
                            });

                            window.location.href = "ContactDataUpload.aspx";        
                        }
                                                
                    }
                    else { 
                        let errorMessage = "Server failed to return a response while submitting contacts incident. Please try your request again.";
                        this.errors.push(errorMessage); //Putting the error in a cute box for the user just so they know what's going on.                       
                        
                        // Throw the error in the dev console just because why not
                        console.error(`${errorMessage} Incident: ${this.incidentNumber} Ori: ${this.ori}`);             
                    }                    
                }                
            },
            handleUpdateTab(componentName:string){
                if(this.validate() && componentName){
                    this.activeTab = componentName;
                }
            },
            validate():boolean {
                let formValid = false;
                let form = document.getElementById("mainForm") as HTMLFormElement;                

                if(form != null && this.$el != null) {
                    formValid = form.reportValidity();
                    if(this.$refs.tabComponent != undefined) {
                        let component = this.$refs.tabComponent as any;
                        formValid = formValid && component.triggerValidation();
                    }

                    if(!formValid && !form.classList.contains('was-validated')){
                        form.classList.add('was-validated');

                        this.scrollToFirstInvalidControl();
                    }

                    if(formValid && form.classList.contains('was-validated')){
                        form.classList.remove('was-validated');
                    }
                }
                
                return formValid;               
            },            
            scrollToFirstInvalidControl() {
                const firstInvalidControl = document.querySelector(".was-validated .form-control:invalid") as HTMLElement;
                
                firstInvalidControl?.focus(); 
            }
        }
    });
</script>
<style src="@vueform/multiselect/themes/default.css"></style>